/**
 * Annexes, common rooms and outdoor areas of a property (docs/adr/0010-flaechen-keller-aussenflaechen.md, docs/15 #18–#21).
 *
 * None of this is engine input. Annexes (Kellerabteil, Dachbodenabteil …) belong to a flat and never count for the area
 * key. Common rooms (Kellerflur, Heizungsraum, Waschkeller …) belong to nobody; they explain cost hints (Allgemeinstrom,
 * maschinelle Wascheinrichtung). Outdoor areas (garden, yard …) with their usage pre-fill "Direktkosten & Nutzergruppen":
 * a garden invoice is split into explicit cost items with the existing scopes (property | units), no new allocation key.
 */

/** Rooms that belong to one flat (Zubehör, § 2 Abs. 3 WoFlV) or are unassigned. */
export const ANNEX_KINDS = ['cellar_room', 'attic_room', 'storage_room', 'parking', 'garage', 'other'] as const;
export type AnnexKind = (typeof ANNEX_KINDS)[number];

/** Rooms used by everybody (or a user group): never a unit, never in any key. */
export const COMMON_ROOM_KINDS = ['cellar_corridor', 'technical_room', 'laundry_room', 'drying_room', 'bike_room', 'stairwell', 'other'] as const;
export type CommonRoomKind = (typeof COMMON_ROOM_KINDS)[number];

export const OUTDOOR_KINDS = ['garden', 'yard', 'terrace', 'playground', 'parking_area', 'hedge_strip', 'other'] as const;
export type OutdoorKind = (typeof OUTDOOR_KINDS)[number];

export type Annex = { id: string; kind: AnnexKind; label: string; unitId: string | null; usableAreaM2?: number };
export type CommonRoom = {
  id: string;
  kind: CommonRoomKind;
  label: string;
  /** A shared washing machine / dryer of the landlord (§ 2 Nr. 16 BetrKV): its costs go only to the users. */
  washingMachine?: boolean;
  /** Units that use the machine (Nutzergruppe); empty = not recorded yet. */
  userUnitIds?: string[];
};
export type OutdoorUsage =
  | { type: 'common' }
  /** Sondernutzung of one or more flats; `maintainedByTenant`: the lease says the tenant looks after it himself. */
  | { type: 'exclusive'; unitIds: string[]; maintainedByTenant: boolean }
  /** Partly common, partly exclusive, e.g. 50 % for everybody, 50 % for the ground-floor flat. */
  | { type: 'mixed'; commonPct: number; unitIds: string[] };
export type OutdoorArea = { id: string; label: string; kind: OutdoorKind; areaM2?: number; usage: OutdoorUsage };

/** Cost types that are about outdoor areas (§ 2 Nr. 10 BetrKV Gartenpflege incl. playground, paths and hedges). */
export const OUTDOOR_COST_CATEGORIES = ['garden'] as const;
export const isOutdoorCategory = (c: string) => (OUTDOOR_COST_CATEGORIES as readonly string[]).includes(c);

/** Unit ids an outdoor area is used by exclusively (empty for common areas). */
export const outdoorUnits = (a: Pick<OutdoorArea, 'usage'>) => (a.usage.type === 'common' ? [] : [...a.usage.unitIds]);
/** Every unit id referenced by annexes, common rooms and outdoor areas (server checks they belong to the property). */
export function estateUnitIds(p: { annexes?: readonly Pick<Annex, 'unitId'>[] | null; commonRooms?: readonly Pick<CommonRoom, 'userUnitIds'>[] | null; outdoorAreas?: readonly Pick<OutdoorArea, 'usage'>[] | null }): string[] {
  const ids = [
    ...(p.annexes ?? []).flatMap((a) => (a.unitId ? [a.unitId] : [])),
    ...(p.commonRooms ?? []).flatMap((r) => r.userUnitIds ?? []),
    ...(p.outdoorAreas ?? []).flatMap(outdoorUnits),
  ];
  return [...new Set(ids)];
}

// ───────────── splitting one invoice ─────────────

export type SplitScope = { kind: 'property' } | { kind: 'units'; unitIds: string[] };
/**
 * One part of a split invoice. `weight` is a positive integer (m² in centi-m² × percent, or a percent in basis points
 * after editing). `tenantMaintained`: the part is for a Sondernutzung the tenant maintains himself, so the landlord
 * bears it (apportionable 'no').
 */
export type SplitPart = { key: string; scope: SplitScope; weight: number; tenantMaintained: boolean; areaIds: string[] };
/** Which area an invoice is for ("Welche Fläche betrifft die Rechnung?"). */
export type SplitTarget = { kind: 'all' } | { kind: 'common' } | { kind: 'units'; unitIds: string[] };
export type SplitPlan = {
  parts: SplitPart[];
  /** area: weights from m² · equal: m² missing, every area counts the same · pct: one mixed area by its share · single: one part only */
  basis: 'area' | 'equal' | 'pct' | 'single';
  /** Areas left out because the tenant maintains them himself (no landlord cost expected there). */
  skippedTenantAreaIds: string[];
};

const unitsKey = (ids: readonly string[]) => [...new Set(ids)].sort().join(',');
const scopeKey = (s: SplitScope) => (s.kind === 'property' ? 'common' : `units:${unitsKey(s.unitIds)}`);

/** The choices of "Welche Fläche betrifft die Rechnung?": all, only common, each Sondernutzung group. */
export function outdoorTargets(areas: readonly OutdoorArea[]): SplitTarget[] {
  const out: SplitTarget[] = [{ kind: 'all' }];
  if (areas.some((a) => a.usage.type !== 'exclusive')) out.push({ kind: 'common' });
  const seen = new Set<string>();
  for (const a of areas) {
    if (a.usage.type === 'common' || !a.usage.unitIds.length) continue;
    const k = unitsKey(a.usage.unitIds);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ kind: 'units', unitIds: k.split(',') });
  }
  return out;
}
export const targetKey = (t: SplitTarget) => (t.kind === 'units' ? `units:${unitsKey(t.unitIds)}` : t.kind);

/** centi-m² of an area, or null when not entered. */
const centi = (a: OutdoorArea) => (a.areaM2 != null && a.areaM2 > 0 ? Math.round(a.areaM2 * 100) : null);

/**
 * Proposed split of an outdoor invoice (Gartenpflege) from the outdoor areas of the property:
 * - common share → whole property (property key), exclusive share → those units (scope units),
 * - mixed → `commonPct` common, the rest to the units,
 * - several areas → by m² when every area has one, otherwise every area counts the same,
 * - an exclusive area the tenant maintains himself is left out of "ganze Außenanlage" (the gardener did not work
 *   there); chosen explicitly it becomes a landlord part (`tenantMaintained`).
 * Hedges: a boundary hedge is recorded as its own common strip (or not at all: it then is part of the common share),
 * a hedge inside a Sondernutzung garden belongs to that garden (ADR-0010 "Hecken"). A common hedge strip without m²
 * joins the common share without weight of its own, so its length never has to be entered.
 */
export function outdoorSplitPlan(areas: readonly OutdoorArea[], target: SplitTarget): SplitPlan {
  if (target.kind === 'common') return { parts: [{ key: 'common', scope: { kind: 'property' }, weight: 1, tenantMaintained: false, areaIds: areas.filter((a) => a.usage.type !== 'exclusive').map((a) => a.id) }], basis: 'single', skippedTenantAreaIds: [] };
  if (target.kind === 'units') {
    const k = unitsKey(target.unitIds);
    const hit = areas.filter((a) => a.usage.type !== 'common' && unitsKey(a.usage.unitIds) === k);
    const tenantMaintained = hit.length > 0 && hit.every((a) => a.usage.type === 'exclusive' && a.usage.maintainedByTenant);
    return { parts: [{ key: `units:${k}`, scope: { kind: 'units', unitIds: k.split(',') }, weight: 1, tenantMaintained, areaIds: hit.map((a) => a.id) }], basis: 'single', skippedTenantAreaIds: [] };
  }
  const skipped = areas.filter((a) => a.usage.type === 'exclusive' && a.usage.maintainedByTenant);
  const candidates = areas.filter((a) => !skipped.includes(a));
  // A common hedge without m² (nobody measures a hedge) adds no weight of its own when another common share exists:
  // it is simply part of the common share. Only when it is the only common share it counts as one area.
  const quietHedge = (a: OutdoorArea) => a.kind === 'hedge_strip' && a.usage.type === 'common' && centi(a) === null;
  const otherCommon = candidates.some((a) => !quietHedge(a) && a.usage.type !== 'exclusive');
  const hedges = otherCommon ? candidates.filter(quietHedge) : [];
  const used = candidates.filter((a) => !hedges.includes(a));
  if (!used.length) return { parts: [{ key: 'common', scope: { kind: 'property' }, weight: 1, tenantMaintained: false, areaIds: [] }], basis: 'single', skippedTenantAreaIds: skipped.map((a) => a.id) };
  const byArea = used.every((a) => centi(a) !== null);
  const parts = new Map<string, SplitPart>();
  const add = (scope: SplitScope, weight: number, areaId: string) => {
    if (weight <= 0) return;
    const key = scopeKey(scope);
    const p = parts.get(key);
    if (p) {
      p.weight += weight;
      if (!p.areaIds.includes(areaId)) p.areaIds.push(areaId);
    } else parts.set(key, { key, scope, weight, tenantMaintained: false, areaIds: [areaId] });
  };
  // common first, so the remainder rule ("Rest auf die letzte Position") hits a Sondernutzung part, as in ADR-0010
  for (const a of used) if (a.usage.type === 'common') add({ kind: 'property' }, (byArea ? centi(a)! : 1) * 100, a.id);
  for (const a of used) if (a.usage.type === 'mixed') add({ kind: 'property' }, (byArea ? centi(a)! : 1) * a.usage.commonPct, a.id);
  for (const a of used) {
    if (a.usage.type === 'common') continue;
    const w = (byArea ? centi(a)! : 1) * (a.usage.type === 'mixed' ? 100 - a.usage.commonPct : 100);
    add({ kind: 'units', unitIds: unitsKey(a.usage.unitIds).split(',') }, w, a.id);
  }
  const common = parts.get('common');
  if (common) for (const h of hedges) common.areaIds.push(h.id);
  const list = [...parts.values()];
  return { parts: list, basis: list.length === 1 ? 'single' : used.length === 1 ? 'pct' : byArea ? 'area' : 'equal', skippedTenantAreaIds: skipped.map((a) => a.id) };
}

/** total × w / W, rounded half away from zero (kaufmännisch), exact with BigInt. */
function shareOf(total: number, w: number, W: number, floor = false): number {
  const n = BigInt(total) * BigInt(w);
  const d = BigInt(W);
  const q = n / d; // truncates toward zero
  const r = n % d;
  if (floor) return Number(q);
  const twice = (r < 0n ? -r : r) * 2n;
  return Number(twice >= d ? q + (n < 0n ? -1n : 1n) : q);
}

/**
 * Splits integer cents by positive integer weights: every part but the last is rounded kaufmännisch, the last part
 * takes the remainder (ADR-0010 "Rest auf die zweite Position", generalised to n parts). Σ parts = total exactly.
 * If rounding would push the last part past zero (only possible for a few cents over three or more parts), all but
 * the last are truncated instead, so no part ever has the opposite sign of the total.
 */
export function splitCents(total: number, weights: readonly number[]): number[] {
  if (!Number.isInteger(total)) throw new Error('total must be integer cents');
  if (!weights.length || weights.some((w) => !Number.isInteger(w) || w <= 0)) throw new Error('weights must be positive integers');
  const W = weights.reduce((s, w) => s + w, 0);
  const run = (floor: boolean) => {
    const head = weights.slice(0, -1).map((w) => shareOf(total, w, W, floor));
    return [...head, total - head.reduce((s, x) => s + x, 0)];
  };
  const out = run(false);
  const last = out[out.length - 1]!;
  return (total >= 0 ? last < 0 : last > 0) ? run(true) : out;
}

/** Money fields of a cost item that a split divides with the same weights. */
export type SplitMoney = { amountCents: number; vatCents?: number; nonApportionableCents?: number; laborCents35a?: number };
/**
 * Divides one invoice into parts: each money field by the same weights with {@link splitCents}, so every field sums
 * back to the invoice exactly. A part's non-apportionable share never exceeds its amount (the excess moves to the next
 * part that has room, the sum stays exact).
 */
export function splitMoney(m: SplitMoney, weights: readonly number[]): SplitMoney[] {
  const amounts = splitCents(m.amountCents, weights);
  const field = (v: number | undefined) => (v == null ? null : splitCents(v, weights));
  const vat = field(m.vatCents);
  const non = field(m.nonApportionableCents);
  const labor = field(m.laborCents35a);
  if (non && m.amountCents >= 0) {
    let carry = 0;
    for (let i = 0; i < non.length; i++) {
      non[i]! += carry;
      carry = 0;
      if (non[i]! > amounts[i]!) {
        carry = non[i]! - amounts[i]!;
        non[i] = amounts[i]!;
      }
    }
    // left over only if non > amount overall; then it stays on the last part (input was inconsistent already)
    if (carry) non[non.length - 1]! += carry;
  }
  return amounts.map((amountCents, i) => ({
    amountCents,
    ...(vat ? { vatCents: vat[i]! } : {}),
    ...(non ? { nonApportionableCents: non[i]! } : {}),
    ...(labor ? { laborCents35a: labor[i]! } : {}),
  }));
}

/** Weights → percentages for display (one decimal, may not add up to exactly 100 due to rounding of the display). */
export const weightPcts = (weights: readonly number[]) => {
  const W = weights.reduce((s, w) => s + w, 0);
  return weights.map((w) => (W ? Math.round((w * 1000) / W) / 10 : 0));
};

/**
 * Percent inputs ("50", "33,33") → integer weights in basis points; null when a value is invalid, zero or the sum is
 * not exactly 100 %.
 */
export function pctWeights(inputs: readonly string[]): number[] | null {
  const bps: number[] = [];
  for (const s of inputs) {
    const m = /^(\d{1,3})(?:[.,](\d{1,2}))?$/.exec(s.trim());
    if (!m) return null;
    const v = Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0'));
    if (v <= 0) return null;
    bps.push(v);
  }
  return bps.reduce((s, x) => s + x, 0) === 10000 ? bps : null;
}

/** Percent text of a weight in basis points for an input ("50", "33,33"). */
export const bpsToInput = (bps: number, sep = ',') => (bps % 100 === 0 ? String(bps / 100) : `${Math.floor(bps / 100)}${sep}${String(bps % 100).padStart(2, '0').replace(/0$/, '')}`);

/** Weights in basis points (sum 10000) that a percent editor starts with; the last part takes the rounding rest. */
export function weightsToBps(weights: readonly number[]): number[] {
  return splitCents(10000, weights);
}
